import React, { useState, useRef, useEffect } from 'react';
import { Button, Tooltip, Upload, message as antMessage } from 'antd';
import {
  SendOutlined,
  PaperClipOutlined,
  StopOutlined,
  MicrophoneOutlined,
} from '@ant-design/icons';
import TextArea from 'antd/es/input/TextArea';
import styles from '../../styles/chatV2.module.css';

const ChatInput = ({
  onSubmit,
  onStop,
  isLoading = false,
  disabled = false,
  placeholder = 'Ask me anything about your data...',
  maxLength = 2000,
}) => {
  const [value, setValue] = useState('');
  const [rows, setRows] = useState(1);
  const textAreaRef = useRef(null);

  // Auto-resize textarea
  useEffect(() => {
    if (textAreaRef.current) {
      const lineHeight = 24;
      const lines = value.split('\n').length;
      const calculatedRows = Math.min(Math.max(lines, 1), 6);
      setRows(calculatedRows);
    }
  }, [value]);

  const handleSubmit = () => {
    if (!value.trim() || isLoading) return;

    onSubmit(value);
    setValue('');
    setRows(1);
  };

  const handleKeyDown = (e) => {
    // Submit on Enter, new line on Shift+Enter
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleFileUpload = ({ file }) => {
    antMessage.info('File attachment feature coming soon');
    return false;
  };

  const characterCount = value.length;
  const showWarning = characterCount > maxLength * 0.9;

  return (
    <div className={styles.chatInputContainer}>
      <div className={styles.inputWrapper}>
        {/* Attachment Button */}
        <Upload
          beforeUpload={handleFileUpload}
          showUploadList={false}
          disabled={disabled || isLoading}
        >
          <Tooltip title="Attach file">
            <Button
              type="text"
              icon={<PaperClipOutlined />}
              className={styles.inputAction}
              disabled={disabled || isLoading}
            />
          </Tooltip>
        </Upload>

        {/* Text Input */}
        <TextArea
          ref={textAreaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          rows={rows}
          autoSize={false}
          className={styles.chatTextArea}
          maxLength={maxLength}
        />

        {/* Send/Stop Button */}
        {isLoading ? (
          <Tooltip title="Stop generating">
            <Button
              type="primary"
              danger
              icon={<StopOutlined />}
              onClick={onStop}
              className={styles.stopButton}
            />
          </Tooltip>
        ) : (
          <Tooltip title="Send message (Enter)">
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSubmit}
              disabled={disabled || !value.trim()}
              className={styles.sendButton}
            />
          </Tooltip>
        )}
      </div>

      {/* Character Count */}
      {characterCount > 0 && (
        <div
          className={`${styles.characterCount} ${
            showWarning ? styles.characterCountWarning : ''
          }`}
        >
          {characterCount} / {maxLength}
        </div>
      )}

      {/* Helper Text */}
      <div className={styles.inputHelper}>
        <span>
          Press <kbd>Enter</kbd> to send, <kbd>Shift + Enter</kbd> for new line
        </span>
      </div>
    </div>
  );
};

export default ChatInput;
